<template>
	<div>
		<b-breadcrumb>
			<b-breadcrumb-item to="/">{{$title}}</b-breadcrumb-item>
			<b-breadcrumb-item active>Commands</b-breadcrumb-item>
		</b-breadcrumb>
		<div class="text-center" v-if="loadingCommands>0">
			<b-icon icon="circle-fill" animation="throb" font-scale="4"></b-icon>
		</div>
		<b-alert show v-else-if="commandsError" variant="danger">{{commandsError&&(commandsError.error||commandsError.toString())}}</b-alert>
		<b-list-group v-else>
			<b-list-group-item v-for="(command, idx) in commands" :key="idx" class="d-flex align-items-center command-item">
				<div class="flex-fill">
					<b-form-checkbox switch v-model="command.status" value="enabled" unchecked-value="disabled" @input="setCommandStatus(command)" :disabled="command.changingStatus">
						<code :style="{textDecoration:command.name_override&&command.name_override!=command.name&&'line-through'}">!{{command.name}}</code><code v-if="command.name_override&&command.name_override!=command.name">&nbsp;!{{command.name_override}}</code>
						<small class="text-muted"> {{command.min_params == -1 ? (command.max_params == -1 ? 'Any' : ` at most ${command.max_params}`) : (command.max_params == -1 ? `at least ${command.min_params}` : (command.max_params == command.min_params ? `${command.min_params}` : `between ${command.min_params} and ${command.max_params}`))}} argument{{command.min_params > 1 || command.max_params > 1 ? 's' : ''}}</small>
					</b-form-checkbox>
					{{command.help_override||command.help}}
				</div>
				<b-button-group size="sm">
					<b-button @click="command.isCustom ? openUserCmdEditModal(command.name) : openCommandEditModal(command.name)" v-b-tooltip.hover.left title="Edit command"><b-icon icon="pencil-fill"></b-icon></b-button>
					<b-button v-if="command.isCustom" variant="danger" @click="openUserCmdDeleteModal(command.name)" v-b-tooltip.hover.right.v-danger title="Delete command"><b-icon icon="trash"></b-icon></b-button>
				</b-button-group>
			</b-list-group-item>
		</b-list-group>
		<div class="text-center mt-3">
			<b-button @click="openUserCmdEditModal()" variant="success"><b-icon icon="plus"></b-icon> Add custom command</b-button>
		</div>
		<b-modal ref="commandEditModal" :title="`Edit command`" :busy="userCmdLoading>0">
			<b-form @submit.stop.prevent="saveCommand(); return false" id="userCommandEditForm" v-if="curCmd">
				<b-form-group label="Name override">
					<b-form-input v-model="curCmd.name_override" maxlength="20"></b-form-input>
				</b-form-group>
				<b-form-group label="Help override">
					<b-form-input v-model="curCmd.help_override"></b-form-input>
				</b-form-group>
				<b-form-group label="Status">
					<b-form-radio-group
						v-model="curCmd.status"
						:options="statuses"
						required
					></b-form-radio-group>
				</b-form-group>
			</b-form>
			<b-alert variant="danger" :show="!!commandError">{{commandError}}</b-alert>
			<template #modal-footer="{ ok, cancel }">
				<b-spinner v-show="userCmdLoading>0" small label="Spinning"></b-spinner>
				<b-button size="sm" variant="success" type="submit" form="userCommandEditForm">
					Save
				</b-button>
				<b-button size="sm" @click="cancel()">
					Cancel
				</b-button>
			</template>
		</b-modal>
		<b-modal ref="userCmdEditModal" :title="`${userCmdName ? 'Edit' : 'Add'} custom command`" :busy="userCmdLoading>0">
			<b-form @submit.stop.prevent="saveUserCmd(); return false" id="userCommandEditForm" v-if="curUserCmd">
				<b-form-group label="Name" description="The command string, without the exclamation mark">
					<b-form-input v-model="curUserCmd.name" required maxlength="20"></b-form-input>
				</b-form-group>
				<b-form-row>
					<b-col>
						<b-form-group label="Minimum arguments" description="use -1 for no minimum">
							<b-form-input v-model="curUserCmd.min_params" type="number" required min="-1"></b-form-input>
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group label="Maximum arguments" description="use -1 for no maximum">
							<b-form-input v-model="curUserCmd.max_params" type="number" required min="-1"></b-form-input>
						</b-form-group>
					</b-col>
				</b-form-row>
				<b-form-group label="Replies" description="Enter one reply per line. Replies are chosen randomly each time the command is invoked. Use $1, $2 for inserting arguments, and $* for all arguments. Special variables include $protocol, $service, $channel, $chat, $bot, $chatter.">
					<b-form-textarea v-model="curUserCmd.data.replies" required></b-form-textarea>
				</b-form-group>
				<b-form-group label="Help">
					<b-form-input v-model="curUserCmd.help"></b-form-input>
				</b-form-group>
				<b-form-group label="Status">
					<b-form-radio-group
						v-model="curUserCmd.status"
						:options="statuses"
						required
					></b-form-radio-group>
				</b-form-group>
			</b-form>
			<b-alert variant="danger" :show="!!userCmdError">{{userCmdError}}</b-alert>
			<template #modal-footer="{ ok, cancel }">
				<b-spinner v-show="userCmdLoading>0" small label="Spinning"></b-spinner>
				<b-button size="sm" variant="success" type="submit" form="userCommandEditForm">
					Save
				</b-button>
				<b-button size="sm" @click="cancel()">
					Cancel
				</b-button>
			</template>
		</b-modal>
		<b-modal ref="userCmdDeleteModal" title="Delete command" @ok="deleteUserCmd()">
			<span v-if="userCmdName">Are you sure you want to delete {{userCmdName}}?</span>
			<b-alert variant="danger" :show="!!userCmdError">{{userCmdError}}</b-alert>
			<template #modal-footer="{ok, cancel}">
				<b-spinner v-show="userCmdLoading>0" small label="Spinning"></b-spinner>
				<b-button size="sm" variant="danger" @click="ok()">
					Delete
				</b-button>
				<b-button size="sm" @click="cancel()">
					Cancel
				</b-button>
			</template>
		</b-modal>
	</div>
</template>

<script>
import util from '../util';

export default {
	data() { return {
		statuses: [ 'enabled', 'disabled' ],

		commandError: null,
		commands: null,
		commandsError: null,
		loadingCommand: 0,
		loadingCommands: 0,
		curCmd: null,

		curUserCmd: null,
		userCmdError: null,
		userCmdLoading: 0,
		userCmdName: null,
		userCommandError: null,
		userCommandsLoading: 0,
	}},
	async created() {
		await this.loadCommands();
	},
	methods: {
		async loadCommands() {
			try {
				this.loadingCommands++;
				this.commandsError = null;
				const [ commands, userCommands ] = await Promise.all([
					this.$api.get('userCommands'),
					this.$api.get('userCustomCommands'),
				]);
				this.commands = [].concat(commands, userCommands.map(c => ({ ...c, isCustom: true }))).map(c => ({ ...c, changingStatus: false }));
			} catch(e) {
				this.commandsError = e;
			} finally {
				this.loadingCommands--;
			}
		},

		async openCommandEditModal(name) {
			try {
				this.commandName = name;
				this.loadingCommand++;
				this.commandError = null;
				this.$refs.commandEditModal.show();
				this.curCmd = await this.$api.get('userCommand', { name });
			} catch(e) {
				console.error('Opening command edit modal', e);
				this.commandError = e;
			} finally {
				this.loadingCommand--;
			}
		},

		async saveCommand() {
			try {
				this.loadingCommand++;
				this.commandError = null;
				await this.$api.post('userCommand', {
					name: this.commandName,
					data: {
						name_override: this.curCmd.name_override,
						help_override: this.curCmd.help_override,
						status: this.curCmd.status,
					},
				});
				this.$refs.commandEditModal.hide();
				this.curCmd = null;
				await this.loadCommands();
			} catch(e) {
				this.commandError = e;
				console.error('Error saving item:', e);
			} finally {
				this.loadingCommand--;
			}
		},

		async setCommandStatus(item) {
			try {
				item.changingStatus = true;
				if(item.isCustom)
					await this.$api.post('userCustomCommandStatus', { name: item.name, status: item.status });
				else
					await this.$api.post('userCommandStatus', { name: item.name, status: item.status });
			} catch(e) {
				console.error('Could not set command status', item.name, item.status, e);
				this.$bvToast.toast('Error while changing command status', {
					title: String(e),
					variant: 'danger',
					solid: true
				});
			} finally {
				item.changingStatus = false;
			}
		},

		async openUserCmdEditModal(name) {
			try {
				this.userCmdError = null;
				this.userCmdLoading++;
				if(name) {
					this.userCmdName = name;
					const cmd = await this.$api.get('userCustomCommand', { name });
					cmd.data.replies = (cmd.data.replies||[]).join('\n');
					this.curUserCmd = cmd;
				} else {
					this.userCmdName = null;
					this.curUserCmd = {
						name: '',
						status: 'enabled',
						data: {},
						min_params: -1,
						max_params: -1,
						help: '',
					};
				}
				this.$refs.userCmdEditModal.show();
			} catch(e) {
				this.userCmdError = e.toString();
			} finally {
				this.userCmdLoading--;
			}
		},

		async saveUserCmd() {
			try {
				this.userCmdLoading++;
				this.userCmdError = null;
				await this.$api.post('userCustomCommand', {
					name: this.userCmdName,
					data: {
						name: this.curUserCmd.name,
						status: this.curUserCmd.status,
						data: {
							replies: (this.curUserCmd.data.replies||'').split('\n').filter(a => !!util.trim(a)),
						},
						min_params: this.curUserCmd.min_params,
						max_params: this.curUserCmd.max_params,
						help: this.curUserCmd.help,
					},
				});
				await this.loadCommands();
				this.$refs.userCmdEditModal.hide();
			} catch(e) {
				console.error('Saving user cmd', e);
				this.userCmdError = e;
			} finally {
				this.userCmdLoading--;
			}
		},

		openUserCmdDeleteModal(name) {
			this.userCmdName = name;
			this.userCmdError = null;
			this.$refs.userCmdDeleteModal.show();
		},

		async deleteUserCmd() {
			try {
				this.userCmdLoading++;
				this.userCmdError = null;
				await this.$api.delete('userCustomCommand', { name: this.userCmdName })
				this.$refs.userCmdDeleteModal.hide();
				this.loadCommands();
			} catch(e) {
				this.userCmdError = e;
			} finally {
				this.userCmdLoading--;
			}
		},
	},
};
</script>

<style scoped lang="scss">
@import '../plugins/variable.scss';

.command-item {
	background-color: $gray-200;
}
.command-item:nth-child(odd) {
	background-color: $gray-400;
}
.command-item:hover {
	background-color: $gray-600;
}
</style>
